import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import LoveLangHeader from "./LoveLangHeader";
import HeartbeatSync from "./HeartbeatSync";

function useUnreadCount() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;

      supabase
        .from("dedications")
        .select("id", { count: "exact", head: true })
        .eq("status", "unread")
        .then(({ count: c }) => setCount(c ?? 0));

      channel = supabase
        .channel("nav-unread-count")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "dedications" }, () =>
          setCount((c) => c + 1)
        )
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "dedications" }, (payload) => {
          const updated = payload.new as { status: string };
          const prev = payload.old as { status: string };
          if (prev.status === "unread" && updated.status !== "unread") {
            setCount((c) => Math.max(0, c - 1));
          } else if (prev.status !== "unread" && updated.status === "unread") {
            setCount((c) => c + 1);
          }
        })
        .subscribe();
    });

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  return count;
}

export default function SiteNav() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const unreadCount = useUnreadCount();

  const links = [
    { to: "/", label: "Listen" },
    { to: "/dedicate", label: "DEDICATE" },
    { to: "/ukhozi", label: "Heard on Ukhozi FM" },
    { to: "/how-it-works", label: "HOW IT'S DONE" },
    { to: "/admin", label: "Studio" },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {/* ── Nav bar ── */}
      <div className="mx-auto flex items-center justify-between h-12 px-4 md:px-6 max-w-screen-xl">
        <Link
          to="/"
          onClick={() => setOpen(false)}
          className="shrink-0"
        >
          <LoveLangHeader size="nav" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`relative inline-flex items-center gap-1.5 t-meta transition-colors whitespace-nowrap ${
                location.pathname === l.to
                  ? "text-absa-red"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {l.label}
              {l.to === "/admin" && unreadCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[14px] h-[14px] px-[3px] rounded-full bg-absa-red text-background font-sans text-[8px] font-bold leading-none tabular-nums">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Link>
          ))}
        </div>

        {/* Right side: Heartbeat Sync + mobile hamburger */}
        <div className="flex items-center gap-3">
          <HeartbeatSync />

          {/* Mobile hamburger — 44px touch target */}
          <button
            className="md:hidden text-muted-foreground hover:text-foreground transition-colors w-11 h-11 flex items-center justify-center -mr-2 btn-press"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="md:hidden bg-background/98 border-b border-border px-4 pb-5 pt-3 flex flex-col gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={`inline-flex items-center gap-2 t-meta transition-colors py-2.5 ${
                location.pathname === l.to ? "text-absa-red" : "text-muted-foreground"
              }`}
            >
              {l.label}
              {l.to === "/admin" && unreadCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[14px] h-[14px] px-[3px] rounded-full bg-absa-red text-background font-sans text-[8px] font-bold leading-none tabular-nums">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
